import { Constants, useMeeting, usePubSub, useMediaDevice } from '@videosdk.live/react-sdk'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/outline'
import useIsRecording from '@/hooks/useIsRecording'
import RecordingIcon from '@/icons/Bottombar/RecordingIcon'
import MicOnIcon from '@/icons/Bottombar/MicOnIcon'
import MicOffIcon from '@/icons/Bottombar/MicOffIcon'
import WebcamOnIcon from '@/icons/Bottombar/WebcamOnIcon'
import WebcamOffIcon from '@/icons/Bottombar/WebcamOffIcon'
import ScreenShareIcon from '@/icons/Bottombar/ScreenShareIcon'
import EndIcon from '@/icons/Bottombar/EndIcon'
import RaiseHandIcon from '@/icons/Bottombar/RaiseHandIcon'
import {
  ClipboardList,
  BookText,
  MessageSquare,
  UsersRound,
  Download,
  Gavel,
  Settings,
  NotebookPen,
  Book,
  ChartPie,
  FileText,
  ClipboardX,
} from 'lucide-react'
import { OutlinedButton } from '@/components/buttons/OutlinedButton'
import { MobileIconButton } from '@/components/buttons/MobileIconButton'
import { debateToolModes, sideBarModes } from '@/utils/common'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import useMediaStream from '@/hooks/useMediaStream'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useHall } from '@/contexts/HallProvider'
import { generateDebatePDF } from '@/lib/generate-pdf'
import { HallUser, SpeakingRole, WinType } from '@db8/shared'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAzureTts } from '@/contexts/AzureTtsProvider'
import { usePoll } from '@/contexts/PollProvider'
import recordingBlink from '@/static/animations/recording-blink.json'
import { useAxios } from '@/contexts/AxiosProvider'
import { useRouter } from '@/hooks/useRouter'
import { toggleSidebarSmart } from '@/utils/sidebarToggle'
import { useSocketContext } from '@/contexts/SocketProvider'

export const RaiseHandBTN = ({ isMobile, isTab }) => {
  const { publish } = usePubSub('RAISE_HAND')
  const RaiseHand = () => {
    publish('Raise Hand', { persist: false })
  }
  return isMobile || isTab ? (
    <MobileIconButton
      btnId="RaiseHandBTN"
      tooltipTitle="Raise hand"
      Icon={RaiseHandIcon}
      onClick={RaiseHand}
      buttonText="Raise Hand"
    />
  ) : (
    <OutlinedButton
      bgColor="bg-gray-200 dark:bg-gray-500 dark:bg-transparent"
      onClick={RaiseHand}
      tooltip="Raise Hand"
      Icon={RaiseHandIcon}
    />
  )
}

export const RecordingBTN = () => {
  const { startRecording, stopRecording, recordingState } = useMeeting()
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: recordingBlink,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
    height: 64,
    width: 160,
  }
  const isRecording = useIsRecording()
  const isRecordingRef = useRef(isRecording)
  useEffect(() => {
    isRecordingRef.current = isRecording
  }, [isRecording])
  const { isRequestProcessing } = useMemo(
    () => ({
      isRequestProcessing:
        recordingState === Constants.recordingEvents.RECORDING_STARTING ||
        recordingState === Constants.recordingEvents.RECORDING_STOPPING,
    }),
    [recordingState],
  )
  const _handleClick = () => {
    if (isRecordingRef.current) {
      stopRecording()
    } else {
      startRecording()
    }
  }
  return (
    <OutlinedButton
      bgColor="bg-gray-200 dark:bg-gray-500 dark:bg-transparent"
      Icon={RecordingIcon}
      onClick={_handleClick}
      isFocused={isRecording}
      tooltip={
        recordingState === Constants.recordingEvents.RECORDING_STARTED
          ? 'Stop Recording'
          : recordingState === Constants.recordingEvents.RECORDING_STARTING
            ? 'Starting Recording'
            : recordingState === Constants.recordingEvents.RECORDING_STOPPED
              ? 'Start Recording'
              : recordingState === Constants.recordingEvents.RECORDING_STOPPING
                ? 'Stopping Recording'
                : 'Start Recording'
      }
      lottieOption={isRecording ? defaultOptions : null}
      isRequestProcessing={isRequestProcessing}
    />
  )
}

export const ScreenShareBTN = ({ isMobile, isTab }) => {
  const { localScreenShareOn, toggleScreenShare, presenterId } = useMeeting()
  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle={
        presenterId ? (localScreenShareOn ? 'Stop Presenting' : null) : 'Present Screen'
      }
      buttonText={presenterId ? (localScreenShareOn ? 'Stop Presenting' : null) : 'Present Screen'}
      Icon={ScreenShareIcon}
      isFocused={localScreenShareOn}
      onClick={toggleScreenShare}
    />
  ) : (
    <OutlinedButton
      bgColor="bg-gray-200 dark:bg-gray-500 dark:bg-transparent"
      focusIconColor="black"
      Icon={ScreenShareIcon}
      onClick={toggleScreenShare}
      isFocused={localScreenShareOn}
      tooltip={presenterId ? (localScreenShareOn ? 'Stop Presenting' : null) : 'Present Screen'}
      disabled={presenterId ? (localScreenShareOn ? false : true) : false}
    />
  )
}

export const LeaveBTN = ({ setIsMeetingLeft }) => {
  const { leave } = useMeeting()
  const { pauseReading } = useAzureTts()
  const { hall } = useHall()
  const socket = useSocketContext()
  const router = useRouter()

  // NEW: pull current user + updater from HallUsersProvider
  const { currentUser, updateHallUsers } = useHallUsers()

  const handleLeave = async () => {
    try {
      // 1) Immediately remove self from hallUsers and publish so others update
      if (currentUser?.id) {
        updateHallUsers((prev) => prev.filter((u) => u.id !== currentUser.id))
      }

      // 2) Notify backend room (meeting code) for count telemetry
      if (hall?.hallId) {
        try {
          socket.emit('leave-hall', { hallId: hall.hallId })
        } catch {}
        // tiny beat so the server can broadcast the new count
        await new Promise((r) => setTimeout(r, 50))
      }

      // 3) Local cleanups
      pauseReading()
      setIsMeetingLeft(true)

      // 4) Leave the VideoSDK meeting
      try {
        leave() // VideoSDK's leave() is sync in your setup
      } catch {}
    } finally {
      router.push('/')
    }
  }

  return (
    <OutlinedButton
      Icon={EndIcon}
      bgColor="bg-red-500"
      color="white"
      borderColor="transparent"
      onClick={handleLeave}
      tooltip="Leave Meeting"
    />
  )
}

export const ChatBTN = ({ isMobile, isTab }) => {
  const { setSideBarMode, secondSideBarMode, sideBarMode, setSecondSideBarMode } =
    useMeetingAppContext()
  const handleClick = (e: React.MouseEvent) => {
    toggleSidebarSmart(sideBarModes.CHAT, {
      sideBarMode,
      secondSideBarMode,
      setSideBarMode,
      setSecondSideBarMode,
      isShift: e?.shiftKey,
    })
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Chat"
      buttonText="Chat"
      Icon={MessageSquare}
      isFocused={sideBarMode === sideBarModes.CHAT || secondSideBarMode === sideBarModes.CHAT}
      onClick={handleClick}
    />
  ) : (
    <OutlinedButton
      Icon={MessageSquare}
      focusIconColor="black"
      onClick={handleClick}
      isFocused={sideBarMode === sideBarModes.CHAT || secondSideBarMode === sideBarModes.CHAT}
      tooltip="View Chat (HOTKEY: c)"
    />
  )
}

export const SequenceBtn = ({ isMobile, isTab }) => {
  const { setSideBarMode, secondSideBarMode, sideBarMode, setSecondSideBarMode } =
    useMeetingAppContext()
  const handleClick = (e: React.MouseEvent) => {
    toggleSidebarSmart(sideBarModes.SEQUENCE, {
      sideBarMode,
      secondSideBarMode,
      setSideBarMode,
      setSecondSideBarMode,
      isShift: e?.shiftKey,
    })
  }
  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Speaker Schedule"
      buttonText="Speaker Schedule"
      Icon={ClipboardList}
      isFocused={
        sideBarMode === sideBarModes.SEQUENCE || secondSideBarMode === sideBarModes.SEQUENCE
      }
      onClick={handleClick}
    />
  ) : (
    <OutlinedButton
      Icon={ClipboardList}
      focusIconColor="black"
      onClick={handleClick}
      isFocused={
        sideBarMode === sideBarModes.SEQUENCE || secondSideBarMode === sideBarModes.SEQUENCE
      }
      tooltip="View Speaker Schedule (HOTKEY: s)"
    />
  )
}

export const TranscriptsBtn = ({ isMobile, isTab }) => {
  const { setSideBarMode, secondSideBarMode, sideBarMode, setSecondSideBarMode } =
    useMeetingAppContext()
  const handleClick = (e: React.MouseEvent) => {
    toggleSidebarSmart(sideBarModes.TRANSCRIPTS, {
      sideBarMode,
      secondSideBarMode,
      setSideBarMode,
      setSecondSideBarMode,
      isShift: e?.shiftKey,
    })
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Transcripts"
      buttonText="Transcripts"
      Icon={BookText}
      isFocused={
        sideBarMode === sideBarModes.TRANSCRIPTS || secondSideBarMode === sideBarModes.TRANSCRIPTS
      }
      onClick={handleClick}
    />
  ) : (
    <OutlinedButton
      Icon={BookText}
      focusIconColor="black"
      onClick={handleClick}
      isFocused={
        sideBarMode === sideBarModes.TRANSCRIPTS || secondSideBarMode === sideBarModes.TRANSCRIPTS
      }
      tooltip="View Transcripts (HOTKEY: t)"
    />
  )
}

export const JudgmentBtn = ({ isMobile, isTab }) => {
  const { setSideBarMode, secondSideBarMode, sideBarMode, setSecondSideBarMode } =
    useMeetingAppContext()
  const handleClick = (e: React.MouseEvent) => {
    toggleSidebarSmart(sideBarModes.JUDGMENT, {
      sideBarMode,
      secondSideBarMode,
      setSideBarMode,
      setSecondSideBarMode,
      isShift: e?.shiftKey,
    })
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Final Judgment"
      buttonText="Final Judgment"
      Icon={Gavel}
      isFocused={
        sideBarMode === sideBarModes.JUDGMENT || secondSideBarMode === sideBarModes.JUDGMENT
      }
      onClick={handleClick}
    />
  ) : (
    <OutlinedButton
      Icon={Gavel}
      focusIconColor="black"
      onClick={handleClick}
      isFocused={
        sideBarMode === sideBarModes.JUDGMENT || secondSideBarMode === sideBarModes.JUDGMENT
      }
      tooltip="Final Judgment (HOTKEY: j)"
    />
  )
}

export const SettingsBtn = ({ isMobile, isTab }) => {
  const { setSideBarMode, secondSideBarMode, sideBarMode, setSecondSideBarMode } =
    useMeetingAppContext()

  const handleClick = (e: React.MouseEvent) => {
    toggleSidebarSmart(sideBarModes.SETTINGS, {
      sideBarMode,
      secondSideBarMode,
      setSideBarMode,
      setSecondSideBarMode,
      isShift: e?.shiftKey,
    })
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Settings"
      buttonText="Settings"
      Icon={Settings}
      isFocused={
        sideBarMode === sideBarModes.SETTINGS || secondSideBarMode === sideBarModes.SETTINGS
      }
      onClick={handleClick}
    />
  ) : (
    <OutlinedButton
      Icon={Settings}
      focusIconColor="black"
      onClick={handleClick}
      isFocused={
        sideBarMode === sideBarModes.SETTINGS || secondSideBarMode === sideBarModes.SETTINGS
      }
      tooltip="Settings (HOTKEY: g)"
    />
  )
}

export const ParticipantsBTN = ({ isMobile, isTab }) => {
  const { setSideBarMode, secondSideBarMode, sideBarMode, setSecondSideBarMode } =
    useMeetingAppContext()
  const { hallUsers } = useHallUsers()

  const handleClick = (e: React.MouseEvent) => {
    toggleSidebarSmart(sideBarModes.PARTICIPANTS, {
      sideBarMode,
      secondSideBarMode,
      setSideBarMode,
      setSecondSideBarMode,
      isShift: e?.shiftKey,
    })
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Participants"
      buttonText="Participants"
      disabledOpacity={1}
      Icon={UsersRound}
      isFocused={
        sideBarMode === sideBarModes.PARTICIPANTS || secondSideBarMode === sideBarModes.PARTICIPANTS
      }
      onClick={handleClick}
      badge={`${hallUsers.length}`}
    />
  ) : (
    <OutlinedButton
      Icon={UsersRound}
      focusIconColor="black"
      onClick={handleClick}
      isFocused={
        sideBarMode === sideBarModes.PARTICIPANTS || secondSideBarMode === sideBarModes.PARTICIPANTS
      }
      tooltip="View Participants (HOTKEY: p)"
      badge={`${hallUsers.length}`}
    />
  )
}

export const FlowNotesBtn = ({ isMobile, isTab, closeDialog = () => {} }) => {
  const { setDebateToolMode } = useMeetingAppContext()

  const handleClick = () => {
    setDebateToolMode((prev) =>
      prev === debateToolModes.FLOWNOTES ? null : debateToolModes.FLOWNOTES,
    )
    closeDialog()
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Flow Notes"
      buttonText="Flow Notes"
      Icon={NotebookPen}
      onClick={handleClick}
      btnId="flowNotesBtn"
    />
  ) : (
    <OutlinedButton
      Icon={NotebookPen}
      onClick={handleClick}
      tooltip="Flow Notes (HOTKEY: f)"
      bgColor="bg-green-600"
      color="white"
      borderColor="transparent"
      btnId="flowNotesBtn"
    />
  )
}

export const PollingBtn = ({ isMobile, isTab, closeDialog = () => {} }) => {
  const { setDebateToolMode } = useMeetingAppContext()

  const handleClick = () => {
    setDebateToolMode((prev) => (prev === debateToolModes.POLL ? null : debateToolModes.POLL))
    closeDialog()
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Polling"
      buttonText="Polling"
      Icon={ChartPie}
      onClick={handleClick}
      btnId="pollingBtn"
    />
  ) : (
    <OutlinedButton
      Icon={ChartPie}
      onClick={handleClick}
      tooltip="Polling (HOTKEY: o)"
      bgColor="bg-pink-600"
      color="white"
      borderColor="transparent"
      btnId="pollingBtn"
    />
  )
}

export const BallotBtn = ({ isMobile, isTab, closeDialog = () => {} }) => {
  const { setDebateToolMode } = useMeetingAppContext()

  const handleClick = () => {
    setDebateToolMode((prev) => (prev === debateToolModes.BALLOT ? null : debateToolModes.BALLOT))
    closeDialog()
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Ballot"
      buttonText="Ballot"
      Icon={ClipboardX}
      onClick={handleClick}
      btnId="ballotBtn"
    />
  ) : (
    <OutlinedButton
      Icon={ClipboardX}
      onClick={handleClick}
      tooltip="Ballot (HOTKEY: a)"
      bgColor="bg-amber-500"
      color="white"
      borderColor="transparent"
      btnId="ballotBtn"
    />
  )
}

export const EvidenceBtn = ({ isMobile, isTab, closeDialog = () => {} }) => {
  const { setDebateToolMode } = useMeetingAppContext()

  const handleClick = () => {
    setDebateToolMode((prev) =>
      prev === debateToolModes.EVIDENCE ? null : debateToolModes.EVIDENCE,
    )
    closeDialog()
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Evidence"
      buttonText="Evidence"
      Icon={FileText}
      onClick={handleClick}
      btnId="evidenceBtn"
    />
  ) : (
    <OutlinedButton
      Icon={FileText}
      onClick={handleClick}
      tooltip="Evidence (HOTKEY: e)"
      borderColor="transparent"
      bgColor="bg-gray-600"
      color="white"
      btnId="evidenceBtn"
    />
  )
}

export const GuidebookBtn = ({ isMobile, isTab, closeDialog = () => {} }) => {
  const { setDebateToolMode } = useMeetingAppContext()

  const handleClick = () => {
    setDebateToolMode((prev) =>
      prev === debateToolModes.GUIDEBOOK ? null : debateToolModes.GUIDEBOOK,
    )
    closeDialog()
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Guidebook"
      buttonText="Guidebook"
      Icon={Book}
      onClick={handleClick}
      btnId="guidebookBtn"
    />
  ) : (
    <OutlinedButton
      Icon={Book}
      onClick={handleClick}
      tooltip="Debate Guidebook (HOTKEY: b)"
      bgColor="bg-indigo-600"
      color="white"
      borderColor="transparent"
      btnId="guidebookBtn"
    />
  )
}

export const ExportBtn = ({ isMobile, isTab }) => {
  const { hall, transcriptions, judgment, ballots, hallMetadata } = useHall()
  const { pollResults } = usePoll()
  const { hallUsers } = useHallUsers()

  const downloadDebatePDF = () => {
    const affirmingTeam = hallUsers.filter((user) => user.speakingRole === SpeakingRole.Affirmative)
    const dissentingTeam = hallUsers.filter((user) => user.speakingRole === SpeakingRole.Negative)

    let analysisContent = null

    if (hall.winType === WinType.AI) {
      analysisContent = judgment
    } else if (hall.winType === WinType.SIMPLEPOLL) {
      const simplePollResults = pollResults as {
        [role in SpeakingRole.Affirmative | SpeakingRole.Negative]: HallUser[]
      }

      analysisContent = {
        type: 'SIMPLEPOLL',
        affirmativeVotes: simplePollResults[SpeakingRole.Affirmative]?.length || 0,
        negativeVotes: simplePollResults[SpeakingRole.Negative]?.length || 0,
        voters: {
          [SpeakingRole.Affirmative]: simplePollResults[SpeakingRole.Affirmative]?.map(
            (u) => u.displayName,
          ),
          [SpeakingRole.Negative]: simplePollResults[SpeakingRole.Negative]?.map(
            (u) => u.displayName,
          ),
        },
      }
    } else if (hall.winType === WinType.EVALUATIONPOLL) {
      const evaluationResults = pollResults as {
        [role in SpeakingRole.Affirmative | SpeakingRole.Negative]: {
          [criterion: string]: number
        }
      }

      analysisContent = {
        type: 'EVALUATIONPOLL',
        criteria: hall.evaluationCriteria,
        scores: {
          [SpeakingRole.Affirmative]: hall.evaluationCriteria.map(
            (c) => evaluationResults[SpeakingRole.Affirmative][c],
          ),
          [SpeakingRole.Negative]: hall.evaluationCriteria.map(
            (c) => evaluationResults[SpeakingRole.Negative][c],
          ),
        },
      }
    }

    const payload = {
      topic: hall?.topic,
      affirmingTeam,
      dissentingTeam,
      transcriptions,
      analysis: analysisContent,
      ballots,
      turns: hallMetadata?.turns,
      flowNotes: (window as any)?.__FLOW_NOTES_PAGES__,
    }

    generateDebatePDF(payload)
  }

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle="Export Debate Data as PDF"
      buttonText="Export"
      Icon={Download}
      onClick={downloadDebatePDF}
      btnId="exportBtn"
    />
  ) : (
    <OutlinedButton
      Icon={Download}
      onClick={downloadDebatePDF}
      tooltip="Export Debate Data as PDF"
      btnId="exportBtn"
    />
  )
}

export const MicBTN = () => {
  const {
    selectedMic,
    setSelectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    isMicrophonePermissionAllowed,
  } = useMeetingAppContext()

  const { getMicrophones, getPlaybackDevices } = useMediaDevice()

  const mMeeting = useMeeting()
  const [mics, setMics] = useState([])
  const [speakers, setSpeakers] = useState([])
  const localMicOn = mMeeting?.localMicOn
  const changeMic = mMeeting?.changeMic

  useMediaDevice({
    onDeviceChanged,
  })

  function onDeviceChanged(devices) {
    getMics()
    const newSpeakerList = devices.devices.filter((device) => device.kind === 'audiooutput')

    if (newSpeakerList.length > 0) {
      setSelectedSpeaker({
        id: newSpeakerList[0].deviceId,
        label: newSpeakerList[0].label,
      })
    }
  }

  const getMics = async () => {
    const mics = await getMicrophones()
    const speakers = await getPlaybackDevices()

    mics && mics?.length && setMics(mics)
    speakers && speakers?.length && setSpeakers(speakers)
  }

  const btnRef = useRef(null)

  return (
    <>
      <OutlinedButton
        Icon={localMicOn ? MicOnIcon : MicOffIcon}
        onClick={() => {
          mMeeting.toggleMic()
        }}
        bgColor="bg-gray-200 dark:bg-gray-500 dark:bg-transparent"
        borderColor={localMicOn ? 'green-400' : 'gray-900'}
        tooltip={'Toggle Mic (HOTKEY: m/Space)'}
        renderRightComponent={() => {
          return (
            <>
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      disabled={!isMicrophonePermissionAllowed}
                      ref={btnRef}
                      className="flex items-center justify-center mt-1 mr-1 focus:outline-none cursor-pointer"
                    >
                      <ChevronDownIcon className="h-4 w-4 text-gray-600 dark:text-white" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="center"
                    className="w-72 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white mb-4"
                  >
                    {/* Microphone Section */}
                    <div className="p-3 pb-0">
                      <p className="text-sm font-semibold">{'MICROPHONE'}</p>
                    </div>
                    <div className="flex flex-col">
                      {mics.length === 0 ? (
                        <DropdownMenuItem
                          key={`mics_default`}
                          disabled
                          className={`px-3 py-1 pl-6 cursor-pointer g-gray-175`}
                        >
                          DEFAULT
                        </DropdownMenuItem>
                      ) : (
                        mics.map(({ deviceId, label }, index) => (
                          <DropdownMenuItem
                            key={`mics_${deviceId}`}
                            onSelect={() => {
                              setSelectedMic({ id: deviceId })
                              changeMic(deviceId)
                            }}
                            className={`px-3 py-1 pl-6 cursor-pointer ${
                              deviceId === selectedMic.id ? 'bg-gray-175' : ''
                            }`}
                          >
                            {label || `Mic ${index + 1}`}
                          </DropdownMenuItem>
                        ))
                      )}
                    </div>
                    <hr className="border border-gray-50 mt-2 mb-1" />
                    {/* Speaker Section */}
                    <div className="p-3 pb-0">
                      <p className="text-sm font-semibold">{'SPEAKER'}</p>
                    </div>
                    <div className="flex flex-col">
                      {speakers.length === 0 ? (
                        <DropdownMenuItem
                          key={`speakers_default`}
                          disabled
                          className={`px-3 py-1 pl-6 cursor-pointer g-gray-175`}
                        >
                          DEFAULT
                        </DropdownMenuItem>
                      ) : (
                        speakers.map(({ deviceId, label }, index) => (
                          <DropdownMenuItem
                            key={`speakers_${deviceId}`}
                            onSelect={() => {
                              setSelectedSpeaker({ id: deviceId })
                            }}
                            className={`px-3 py-1 pl-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                              deviceId === selectedSpeaker.id ? 'bg-gray-175' : ''
                            }`}
                          >
                            {label || `Speaker ${index + 1}`}
                          </DropdownMenuItem>
                        ))
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          )
        }}
      />
    </>
  )
}

export const WebCamBTN = ({ toggleWebcam }) => {
  const { selectedWebcam, setSelectedWebcam, isCameraPermissionAllowed } = useMeetingAppContext()

  const { getCameras } = useMediaDevice()
  const mMeeting = useMeeting()
  const [webcams, setWebcams] = useState([])
  const { getVideoTrack } = useMediaStream()

  const localWebcamOn = mMeeting?.localWebcamOn
  const changeWebcam = mMeeting?.changeWebcam

  const getWebcams = async () => {
    let webcams = await getCameras()
    webcams && webcams?.length && setWebcams(webcams)
  }

  const btnRef = useRef(null)

  useEffect(() => {
    getWebcams()
  }, [])
  return (
    <>
      <OutlinedButton
        Icon={localWebcamOn ? WebcamOnIcon : WebcamOffIcon}
        onClick={toggleWebcam}
        bgColor="bg-gray-200 dark:bg-gray-500 dark:bg-transparent"
        borderColor={localWebcamOn ? 'green-400' : 'gray-900'}
        tooltip={'Toggle Webcam (HOTKEY: w)'}
        renderRightComponent={() => {
          return (
            <>
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      disabled={!isCameraPermissionAllowed}
                      ref={btnRef}
                      className="flex items-center justify-center mt-1 mr-1 focus:outline-none cursor-pointer"
                    >
                      <ChevronDownIcon className="h-4 w-4 text-gray-600 dark:text-white" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="center"
                    className="w-72 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white mb-4"
                  >
                    <div className="flex flex-col ">
                      {webcams.map(({ deviceId, label }, index) => (
                        <DropdownMenuItem
                          key={`output_webcams_${deviceId}`}
                          onSelect={() => {
                            setSelectedWebcam({ id: deviceId })
                            changeWebcam(deviceId)
                          }}
                          className={`px-3 py-1 pl-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800  ${
                            deviceId === selectedWebcam.id ? 'bg-gray-175' : ''
                          }`}
                        >
                          {label || `Webcam ${index + 1}`}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          )
        }}
      />
    </>
  )
}
